import { eventTitle } from '@/functions/eventDisplay';

// Fonction pour nettoyer le lieu (supprime les parenthèses et leur contenu)
const cleanPlace = (place: string | undefined | null): string => {
  if (!place) return '';
  return place.replace(/\s*\([^)]*\)/g, '').trim();
};

// --- Logique pour les événements ---

const parseEventStartDate = (event: OfficialEvent): Date => {
  return new Date(event.date + 'T09:00:00');
};

const parseEventEndDate = (event: OfficialEvent): Date => {
  const endStr = event.date_end || event.date;
  return new Date(endStr + 'T21:00:00');
};

// Fonction pour générer un lien Google Calendar pour un événement
const getGoogleCalendarLink = (event: OfficialEvent) => {
  const startDate = parseEventStartDate(event);
  const endDate = parseEventEndDate(event);

  const formatDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const details = encodeURIComponent(event.description || '');
  const location = encodeURIComponent(cleanPlace(event.place));
  const name = encodeURIComponent('Tournoi Fléchettes : ' + eventTitle(event) + ' 🎯' || '');

  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${name}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${details}&location=${location}&sf=true&output=xml`;
};

// Fonction pour générer un lien Apple Calendar pour un événement (fichier .ics)
const getAppleCalendarLink = (event: OfficialEvent) => {
  const startDate = parseEventStartDate(event);
  const endDate = parseEventEndDate(event);

  const formatICSDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:${eventTitle(event) || 'Événement'}`,
    `DESCRIPTION:${event.description || ''}`,
    `LOCATION:${cleanPlace(event.place)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return 'data:text/calendar;charset=utf8,' + encodeURIComponent(icsContent);
};

// Fonction pour obtenir le lien de calendrier pour un événement
export const getCalendarLink = (event: OfficialEvent): { href: string; download?: string } => {
  try {
    const isAppleDevice = /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);
    if (isAppleDevice) {
      return {
        href: getAppleCalendarLink(event),
        download: `${eventTitle(event)}.ics`,
      };
    }
    return {
      href: getGoogleCalendarLink(event),
    };
  } catch {
    return { href: '#' };
  }
};

// --- Logique pour les tournois ---

// Fonction pour parser la date de début du tournoi
const parseTournamentDate = (dateStr: string): Date => {
  return new Date(dateStr); // Format ISO (ex. "2025-09-04T19:30:00")
};

// Fonction pour parser la date de fin du tournoi (23h00 le même jour)
const parseTournamentEndDate = (dateStr: string): Date => {
  const startDate = new Date(dateStr);
  const endDate = new Date(startDate);
  endDate.setHours(23, 0, 0, 0);
  return endDate;
};

// Fonction pour générer un lien Google Calendar pour un tournoi
const getGoogleCalendarLinkTournament = (tournament: Tournament) => {
  const startDate = parseTournamentDate(tournament.start_date);
  const endDate = parseTournamentEndDate(tournament.start_date);

  const formatDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const details = encodeURIComponent(tournament.description || '');
  const location = encodeURIComponent(cleanPlace(tournament.description));
  const name = encodeURIComponent('Tournoi Fléchettes Badarts : ' + tournament.name + ' 🎯' || '');

  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${name}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${details}&location=${location}&sf=true&output=xml`;
};

// Fonction pour générer un lien Apple Calendar pour un tournoi (fichier .ics)
const getAppleCalendarLinkTournament = (tournament: Tournament) => {
  const startDate = parseTournamentDate(tournament.start_date);
  const endDate = parseTournamentEndDate(tournament.start_date);

  const formatICSDate = (date: Date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'BEGIN:VEVENT',
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:${tournament.name || 'Tournoi'}`,
    `DESCRIPTION:${tournament.description || ''}`,
    `LOCATION:${cleanPlace(tournament.description)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return 'data:text/calendar;charset=utf8,' + encodeURIComponent(icsContent);
};

// Fonction pour obtenir le lien de calendrier pour un tournoi
export const getCalendarLinkTournament = (tournament: Tournament): { href: string; download?: string } => {
  const isAppleDevice = /iPhone|iPad|iPod|Macintosh/i.test(navigator.userAgent);
  if (isAppleDevice) {
    return {
      href: getAppleCalendarLinkTournament(tournament),
      download: `${tournament.name}.ics`,
    };
  }
  return {
    href: getGoogleCalendarLinkTournament(tournament),
  };
};